import { type InputHTMLAttributes, useState } from 'react';
import type { FieldError, FieldPath, FieldValues } from 'react-hook-form';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Icon } from '../icon';
import { FormField } from './FormField';
import { useDebouncedFieldValidation } from './useDebouncedFieldValidation';
import { type FormFieldOwnProps, useFormField } from './useFormField';

export type FormPasswordFieldProps<T extends FieldValues = FieldValues, N extends FieldPath<T> = FieldPath<T>> = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'name' | 'value' | 'onChange' | 'onBlur' | 'type'
> &
  FormFieldOwnProps<T, N> & {
    debounceMs?: number;
    /** Accessible name for the toggle button when the password is hidden (i.e. clicking will show it). */
    showPasswordLabel?: string;
    /** Accessible name for the toggle button when the password is visible (i.e. clicking will hide it). */
    hidePasswordLabel?: string;
    /** Suppress the visibility-toggle button (e.g. on a confirm-password field). */
    hideToggle?: boolean;
    /**
     * Called with the field's current error, if any; return `true` to hide its message (and
     * its `aria-describedby` link) while still leaving the field marked invalid. For a
     * companion checklist (e.g. PasswordStrengthIndicator) that already shows a specific
     * validation rule inline — the caller identifies which error that is, since only it knows
     * its own schema.
     */
    suppressError?: (error: FieldError) => boolean;
  };

/**
 * Password input with visibility toggle.
 *
 * Depending on use-case, the caller should set `autoComplete="new-password"` (registration)
 * or `autoComplete="current-password"` (login) to enable correct browser autofill behavior.
 *
 * The toggle button's accessible name defaults to German (the platform's primary
 * locale); pass `showPasswordLabel` / `hidePasswordLabel` from your i18n layer to
 * override.
 */
export function FormPasswordField<T extends FieldValues = FieldValues, N extends FieldPath<T> = FieldPath<T>>({
  name,
  label,
  helperText,
  className,
  disabled,
  debounceMs,
  showPasswordLabel = 'Passwort anzeigen',
  hidePasswordLabel = 'Passwort verbergen',
  hideToggle = false,
  suppressError,
  'aria-describedby': ariaDescribedBy,
  ...inputProps
}: FormPasswordFieldProps<T, N>) {
  const [showPassword, setShowPassword] = useState(false);
  const { fieldProps, formFieldProps } = useFormField<T, N>({ name, label, helperText, className });
  useDebouncedFieldValidation<T, N>(name, debounceMs);

  const errorSuppressed = formFieldProps.error !== undefined && suppressError?.(formFieldProps.error) === true;
  const visibleError = errorSuppressed ? undefined : formFieldProps.error;

  // Merge any caller-supplied describedby (e.g. a password-requirements list) with the field's own
  // error/helper association so neither clobbers the other. Field error/helper comes first so it's
  // announced ahead of supplementary guidance. When the error message itself is suppressed, FormField
  // falls back to rendering helperText instead, so point at that id rather than dropping it.
  const fieldDescribedBy = errorSuppressed ? (helperText ? `${fieldProps.id}-helper` : undefined) : fieldProps['aria-describedby'];
  const describedBy = [fieldDescribedBy, ariaDescribedBy].filter(Boolean).join(' ') || undefined;

  const toggleButton = hideToggle ? undefined : (
    <Button
      type="button"
      variant="plain"
      shape="pill"
      size="icon"
      iconOnly
      onClick={() => setShowPassword((prev) => !prev)}
      disabled={disabled}
      aria-label={showPassword ? hidePasswordLabel : showPasswordLabel}
      className="size-auto"
    >
      <Icon name={showPassword ? 'eye-open' : 'eye-closed'} size="md" />
    </Button>
  );

  return (
    <FormField {...formFieldProps} error={visibleError}>
      <Input
        {...fieldProps}
        {...inputProps}
        aria-describedby={describedBy}
        spellCheck={false}
        autoCapitalize="off"
        autoCorrect="off"
        inputMode="text"
        disabled={disabled}
        type={showPassword && !hideToggle ? 'text' : 'password'}
        iconRight={toggleButton}
      />
    </FormField>
  );
}
